import {
  useMemo,
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import dragAnimation from "../../assets/ClickDragAnimation.json";
import Lottie from "lottie-react";
import Subtitle from "../Subtitles/Subtitle";
import Translation from "../Subtitles/Translation";
import { UserVideoWord } from "@shared_types";
import AddWordButton from "../Buttons/AddWordButton";
import ReportError from "../ReportError";
import { usePhrase } from "./usePhrase";
import { HighlightedWordsRef } from "../Subtitles/HighlightedWords";
import AddPhraseButton from "../Buttons/AddPhraseButton";
import ToggleButton from "../Buttons/ToggleButton";
import { isInvalidDictionaryWord } from "../Subtitles/invalidDictionaryWord";
import { useTrackWordClicked } from "../../hooks/posthog/useTrackWordClicked";

interface VideoContentProps {
  lessonId: number;
  currentTime: number;
  userVideoWords: UserVideoWord[];
  onWordClick: (clickedWord: UserVideoWord) => void;
  onKnownClick?: (word: UserVideoWord) => void;
  onPhraseSelectStart?: () => void;
  isDisplayHelp: boolean;
  targetWord: string;
  selectedVideoWordId: number;
  nativeWord: string;
  selectedWordStatus: string;
  clickHelp: string;
  isHorizontal: boolean;
}

type ScreenSize = {
  width: number;
  height: number;
};

const useScreenSize = (): ScreenSize => {
  const [screenSize, setScreenSize] = useState<ScreenSize>(() => {
    // Initial size calculation
    if (typeof window !== "undefined") {
      const width = window.innerWidth;
      const height = window.innerHeight;
      return { width, height };
    }
    return { width: 0, height: 0 }; // Default fallback
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setScreenSize({ width, height });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return screenSize;
};

// Add an export interface for the VideoContent ref
export interface VideoContentRef {
  clearPhraseSelection: () => void;
}

// Update VideoContent to use forwardRef
const VideoContent = forwardRef<VideoContentRef, VideoContentProps>(
  (
    {
      lessonId,
      currentTime,
      userVideoWords,
      selectedVideoWordId,
      onWordClick,
      onKnownClick,
      onPhraseSelectStart,
      isDisplayHelp,
      targetWord,
      nativeWord,
      selectedWordStatus,
      clickHelp,
    },
    ref
  ) => {
    const screenSize = useScreenSize();
    const trackWordClicked = useTrackWordClicked();
    // Add a ref for the HighlightedWords component
    const highlightedWordsRef = useRef<HighlightedWordsRef>(null);

    // Expose the clearPhraseSelection method to parent components
    useImperativeHandle(ref, () => ({
      clearPhraseSelection: () => {
        // Call the HighlightedWords clearPhraseSelection method
        highlightedWordsRef.current?.clearPhraseSelection();
        // Also clear the local phrase selection state
        clearPhraseSelection();
      },
    }));

    const paragraphRef = highlightedWordsRef.current?.getParagraphRef();
    const [paragraphSize, setParagraphSize] = useState<DOMRect | null>(null);
    const [fontSize, setFontSize] = useState<string>("16px");
    const [fontFamily, setFontFamily] = useState<string>("Inter, sans-sarif");

    useEffect(() => {
      if (!paragraphRef) return;

      const updateSize = () => {
        const size = paragraphRef.getBoundingClientRect();
        setParagraphSize(size);
        const computedStyle = getComputedStyle(paragraphRef);
        const fontSize = computedStyle.fontSize;
        const fontFamily = computedStyle.fontFamily;
        setFontSize(fontSize);
        setFontFamily(fontFamily);
      };

      updateSize(); // Initial measurement

      const observer = new ResizeObserver(() => {
        updateSize(); // Re-measure on resize
      });

      observer.observe(paragraphRef);

      return () => observer.disconnect();
    }, [paragraphRef]);

    const fontForPivots = `${fontSize} ${fontFamily}`;
    const defaultPsizePx = 192;
    // we need to get the word here.
    // we can get that from highlight index
    // This component will rerender whene ver the curretTimeState changes in the parents
    // current time state drives each render we need to recalc all of them
    // all the display for this component is derived from the parent time state
    const pivots = useMemo(
      () =>
        calculatePivotsByPixelWidth(
          userVideoWords,
          paragraphSize?.width || defaultPsizePx,
          fontForPivots
        ),
      [userVideoWords, screenSize, fontForPivots]
    );
    const highlightIndex = calcWordDisplayIndex(currentTime, userVideoWords);
    // if not found show the last pivot
    const selectedPivot =
      pivots.find(
        (pivot) => pivot[0] <= highlightIndex && highlightIndex <= pivot[1]
      ) || pivots[pivots.length - 1];
    const isKnown = selectedWordStatus == "known";
    const isDeleted = selectedWordStatus == "deleted";

    // Phrase selection state
    // to track when the user is dragging
    // to track if the user has a phrase selection in the selecton box
    const [hasPhraseSelection, setHasPhraseSelection] = useState(false);
    // track the start ID of the selected word
    const [phraseStartWordId, setPhraseStartWordId] = useState<
      number | undefined
    >(undefined);
    // track the end ID of the selected word
    const [phraseEndWordId, setPhraseEndWordId] = useState<number | undefined>(
      undefined
    );

    let firstWordIdx = 0;
    let lastWordIdx = 0;
    let leftMostWordIdx = 0;
    let rightMostWordIdx = 0;
    let selectedPhraseString = "";
    if (phraseStartWordId !== undefined && phraseEndWordId !== undefined) {
      firstWordIdx = userVideoWords.findIndex(
        (word) => word.videoWordId === phraseStartWordId
      );
      lastWordIdx = userVideoWords.findIndex(
        (word) => word.videoWordId === phraseEndWordId
      );
      leftMostWordIdx = Math.min(firstWordIdx, lastWordIdx);
      rightMostWordIdx = Math.max(firstWordIdx, lastWordIdx);
      selectedPhraseString = userVideoWords
        .slice(leftMostWordIdx, rightMostWordIdx + 1)
        .map((word) => word.displayText)
        .join(" ");
    }

    const {
      phraseTranslation,
      isLoading: isPhraseLoading,
      isError,
      phraseId,
      isPhraseSaved,
      setIsPhraseSaved,
    } = usePhrase(
      // the start and end does not mean or say anything about left or right of the word,
      // it just is the about how the user decided to select the phrase.
      phraseStartWordId,
      phraseEndWordId,
      selectedPhraseString
    );

    const handlePhraseSelectStart = () => {
      setPhraseStartWordId(undefined);
      setPhraseEndWordId(undefined);
      if (onPhraseSelectStart) {
        // bubble event up to the parent
        onPhraseSelectStart();
      }
    };

    const handlePhraseSelectDone = (
      startWord: UserVideoWord,
      endWord: UserVideoWord
    ) => {
      // dont allow length of 1
      if (startWord.videoWordId === endWord.videoWordId) {
        return;
      }
      // Save the word IDs for highlighting
      setPhraseStartWordId(startWord.videoWordId);
      setPhraseEndWordId(endWord.videoWordId);
      // Mark that we have a selection
      setHasPhraseSelection(true);
    };

    // Clear the phrase selection
    const clearPhraseSelection = () => {
      setHasPhraseSelection(false);
      setPhraseStartWordId(undefined);
      setPhraseEndWordId(undefined);
    };

    // Custom onWordClick handler to clear phrase selection when a new word is clicked
    const handleWordClick = (clickedWord: UserVideoWord) => {
      // Clear any phrase selection
      clearPhraseSelection();
      // Send the word clicked event
      trackWordClicked();

      // Call the original onWordClick handler
      onWordClick(clickedWord);
    };

    const handleKnownClick = () => {
      const word = userVideoWords[highlightIndex];
      if (onKnownClick) {
        onKnownClick(word);
      }
    };

    // fix out of bounds if we screwed up
    if (selectedPivot[1] >= userVideoWords.length) {
      console.log(
        "WARNING: out of bounds selectedPivot[1] >= userVideoWords.length"
      );
      selectedPivot[1] = userVideoWords.length - 1;
    }

    const toShowUserWords = userVideoWords.slice(
      selectedPivot[0],
      selectedPivot[1] + 1
    );

    const roundedNessCls = "rounded-xl";

    // this is the index relative to the words that are being shown
    const showWordsHighlightIndex = highlightIndex - selectedPivot[0];
    return (
      <div
        className={`h-full relative flex flex-col justify-start w-full ${roundedNessCls} p-4 flex-grow bg-[#fafafa]`}
      >
        <ReportError
          lessonId={lessonId}
          videoWordId={selectedVideoWordId}
          targetWord={targetWord}
          clickHelp={clickHelp}
          isDisplayHelp={isDisplayHelp}
          leftControlClassName="bottom-2 left-[8px]"
          rightControlClassName="bottom-2 right-[8px]"
        />
        <>
          <Subtitle
            ref={highlightedWordsRef}
            highlightIndex={showWordsHighlightIndex}
            userVideoWords={toShowUserWords}
            onWordClick={handleWordClick}
            onDoubleWordClick={handleKnownClick}
            onPhraseSelectedDone={handlePhraseSelectDone}
            onPhraseSelectStart={handlePhraseSelectStart}
          />
          {isDisplayHelp ? (
            <div className="flex flex-col h-full items-center text-center justify-center relative">
              <div className="relative">
                <Lottie
                  animationData={dragAnimation}
                  loop={true}
                  className={`absolute bottom-5 left-1/2 -translate-x-1/2 z-0 h-16 w-16`}
                />
                <div className="font-semibold bg-[#fafafa] pt-12">
                  Try clicking or highlighting the words!
                </div>
              </div>
            </div>
          ) : (
            <div className="pt-4">
              {hasPhraseSelection ? (
                <Translation
                  phraseTranslation={phraseTranslation}
                  targetWord={targetWord}
                  nativeWord={nativeWord}
                  isPhraseLoading={isPhraseLoading}
                  isPhraseError={isError}
                />
              ) : (
                <Translation targetWord={targetWord} nativeWord={nativeWord} />
              )}
            </div>
          )}
          {!isDisplayHelp && (
            <>
              <div className="mt-auto pt-2 flex flex-row-reverse">
                {(() => {
                  if (hasPhraseSelection) {
                    if (isPhraseLoading) {
                      return (
                        <div>
                          {/* to keep the sizing of the divs the same make an invisible button rather than removing it fully */}
                          <ToggleButton
                            isActive={isPhraseSaved}
                            activeText="Loading..."
                            inactiveText="Loading..."
                            onClick={() => {}}
                            disabled={true}
                            className="w-[101px]"
                          />
                        </div>
                      );
                    }
                    if (phraseId === undefined) {
                      return (
                        <div className="invisible">
                          {/* to keep the sizing of the divs the same make an invisible button rather than removing it fully */}
                          <AddPhraseButton
                            isSaved={isPhraseSaved}
                            onClick={() => {}}
                          />
                        </div>
                      );
                    }
                    return (
                      <AddPhraseButton
                        isSaved={isPhraseSaved}
                        onClick={() => {
                          setIsPhraseSaved({
                            phraseId: phraseId,
                            isSaved: !isPhraseSaved,
                            lessonId: lessonId,
                          });
                        }}
                      />
                    );
                  } else if (
                    isInvalidDictionaryWord(
                      toShowUserWords[showWordsHighlightIndex]?.displayText ||
                        ""
                    )
                  ) {
                    return null;
                  } else if (onKnownClick) {
                    return (
                      <AddWordButton
                        isKnown={isKnown}
                        isDeleted={isDeleted}
                        onClick={handleKnownClick}
                      />
                    );
                  }
                  return null;
                })()}
              </div>
            </>
          )}
        </>
      </div>
    );
  }
);

export default VideoContent;

// Export the utility functions
export function calculatePivotsByPixelWidth(
  words: UserVideoWord[],
  containerWidth: number,
  font: string
): [number, number][] {
  const leftAndRightPaddingPx = 6;
  const spacePaddingPx = 4;
  const experimentallyObservedErrorPx = 4;
  const wordSpacingPx = leftAndRightPaddingPx + spacePaddingPx;
  const pivots: [number, number][] = [];
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;
  ctx.font = font;

  let startIndex = 0;
  let i = 0;
  const total = words.length;

  while (i < total) {
    let currentLineWidth = 0;
    let lines = 1;
    let pivotEnd = i;

    for (let j = i; j < total; j++) {
      const word = words[j].displayText;
      let wordWidth = 0;

      wordWidth = ctx.measureText(word).width + wordSpacingPx;

      const nextWidth =
        currentLineWidth + wordWidth + experimentallyObservedErrorPx;

      if (nextWidth >= containerWidth) {
        if (lines === 2) {
          // Check lookahead: would the next chunk be very small?
          let lookaheadWidth = 0;
          let lookaheadIndex = j;

          for (lookaheadIndex = j; lookaheadIndex < total; lookaheadIndex++) {
            const laWord = words[lookaheadIndex].displayText;
            const laWidth =
              ctx.measureText(laWord).width +
              wordSpacingPx +
              experimentallyObservedErrorPx;

            lookaheadWidth += laWidth;

            if (lookaheadWidth > containerWidth / 3) break;
          }

          const nextChunkIsSmall = lookaheadWidth <= containerWidth / 3;

          if (nextChunkIsSmall) {
            // Absorb into current pivot
            pivotEnd = lookaheadIndex - 1;
            i = pivotEnd + 1;
            pivots.push([startIndex, pivotEnd]);
            startIndex = i;
            break;
          }

          break; // normal case: stop at 2 lines
        }

        // start new line
        lines++;
        currentLineWidth = wordWidth;
      } else {
        currentLineWidth = nextWidth;
      }

      pivotEnd = j;
    }

    if (i <= pivotEnd) {
      pivots.push([startIndex, pivotEnd]);
      i = pivotEnd + 1;
      startIndex = i;
    } else {
      break;
    }
  }

  return pivots;
}

export function calcWordDisplayIndex(time: number, arr: UserVideoWord[]) {
  if (arr.length < 1) {
    return 0;
  } else {
    const firstWord = arr[0];
    if (firstWord.start >= time) {
      return 0;
    }
  }
  for (let i = 0; i < arr.length; i++) {
    const word = arr[i];
    // hit the end of the array we can return the last word
    if (i == arr.length - 1) {
      return i;
    }
    const nextWord = arr[i + 1];
    const wordEnd = word.start + word.duration;
    const spaceBetweenNextWordAndWordEnd = nextWord.start - wordEnd;
    const halfWayPoint = wordEnd + spaceBetweenNextWordAndWordEnd / 2;

    // if we had no word duration information we we should use the three querter point instead
    // of the midway point for switching words
    let wordPivot = halfWayPoint;
    if (word.duration === 0) {
      wordPivot = nextWord.start;
    }
    // Check if the current time falls within the range of the current word
    if (word.start <= time && time < wordPivot) {
      return i; // Highlight the current word
      // checking if time is before the next word
    } else if (wordPivot <= time && time < nextWord.start) {
      return i + 1;
    }
  }
  return 0; // Default to the first word if time is beyond the range
}

